<script>
/**
 * @component Validation
 * @description Pantalla de validación de prestaciones médicas. Flujo doctor_ui.
 * El prestador es siempre el doctor logueado — el backend lo toma del token de sesión.
 * Capas: elegibilidad del afiliado → cobertura del plan → habilitación del prestador → valorización.
 */
import memberService from "@/services/member";
import validationService from "@/services/validation";
import debounce from "debounce";

const DEBOUNCE_MS = 400;

const REJECTION_LABELS = {
  member_inactive: "El afiliado no está activo",
  member_no_plan: "El afiliado no tiene un plan activo",
  invalid_token: "Token inválido",
  practice_not_covered: "La prestación no está cubierta por el plan",
  provider_inactive: "El prestador no está activo",
  provider_no_contract:
    "El prestador no tiene contrato vigente para esta prestación",
};

export default {
  name: "Validation",
  data() {
    return {
      // Búsqueda de afiliado
      searchInput: "",
      searchingPatient: false,
      patientFound: null,
      patientSelected: null,
      errorPatient: null,

      // Selección de prestación
      practiceId: "",
      practiceOptions: [],
      practiceSearchLoading: false,
      practiceOptionsLoaded: false,

      // Token
      token: "",

      // Resultado
      validatingCode: false,
      validationResult: null,
      errorCode: null,
    };
  },
  computed: {
    /** @returns {number} Paso activo del stepper (1, 2 o 3) */
    currentStep() {
      if (!this.patientSelected) return 1;
      if (!this.practiceId) return 2;
      return 3;
    },
    /** @returns {string} Iniciales del afiliado seleccionado */
    patientInitials() {
      if (!this.patientSelected) return "";
      return (
        (this.patientSelected.firstName || "")[0] +
        (this.patientSelected.lastName || "")[0]
      ).toUpperCase();
    },
    /** @returns {string} Nombre completo del afiliado seleccionado */
    patientName() {
      if (!this.patientSelected) return "";
      return `${this.patientSelected.lastName}, ${this.patientSelected.firstName}`;
    },
    /** @returns {string} Resumen del afiliado seleccionado */
    patientSummary() {
      if (!this.patientSelected) return "";
      const parts = [];
      if (this.patientSelected.dni)
        parts.push(`DNI ${this.patientSelected.dni}`);
      if (this.patientSelected.memberNumber)
        parts.push(`Afil. ${this.patientSelected.memberNumber}`);
      const planName = this.patientSelected.plan?.name || "";
      if (planName) parts.push(planName);
      return parts.join(" · ");
    },
    /** @returns {string[]} Motivos de rechazo con label legible */
    rejectionLabels() {
      if (!this.validationResult || !this.validationResult.rejectionReasons)
        return [];
      return this.validationResult.rejectionReasons.map(
        (r) => REJECTION_LABELS[r] || r
      );
    },
  },
  methods: {
    /**
     * @method searchPatient
     * @desc Busca afiliados por Nº de afiliado o DNI
     */
    async searchPatient() {
      if (!this.searchInput.trim()) {
        this.errorPatient = "Ingresá un Nº de afiliado o DNI";
        return;
      }
      this.searchingPatient = true;
      this.errorPatient = null;
      this.patientFound = null;
      try {
        const res = await memberService.query({
          searchInput: this.searchInput.trim(),
          limit: 10,
        });
        const docs = res.docs || [];
        if (docs.length > 0) {
          this.patientFound = docs;
        } else {
          this.errorPatient = "No se encontró ningún afiliado con esos datos";
        }
      } catch {
        this.errorPatient = "Error al buscar el afiliado";
      } finally {
        this.searchingPatient = false;
      }
    },

    /**
     * @method selectPatient
     * @desc Selecciona un afiliado de la lista y avanza al paso 2
     * @param {Object} patient
     */
    selectPatient(patient) {
      this.patientSelected = patient;
      this.patientFound = null;
    },

    /**
     * @method searchPractices
     * @desc Busca prestaciones del contrato activo del prestador logueado
     * @param {string} query
     */
    searchPractices: debounce(async function (query) {
      this.practiceSearchLoading = true;
      try {
        const res = await validationService.getMyPractices(query);
        this.practiceOptions = (res.docs || []).map((p) => ({
          value: p._id,
          label: `${p.code} - ${p.name}`,
        }));
      } finally {
        this.practiceSearchLoading = false;
      }
    }, DEBOUNCE_MS),

    /**
     * @method validateCode
     * @desc Llama al motor de validación del backend y muestra el resultado por capas
     */
    async validateCode() {
      if (!this.token.trim()) {
        this.errorCode = "Ingresá el token del afiliado";
        return;
      }
      if (!this.patientSelected?._id) {
        this.errorCode = "Seleccioná un afiliado";
        return;
      }
      if (!this.practiceId) {
        this.errorCode = "Seleccioná una prestación";
        return;
      }

      this.validatingCode = true;
      this.errorCode = null;
      this.validationResult = null;

      try {
        this.validationResult = await validationService.validate(
          this.patientSelected._id,
          this.practiceId,
          this.token
        );
      } catch {
        this.errorCode = "Error al procesar la validación";
      } finally {
        this.validatingCode = false;
      }
    },

    /** @method clearAll - Limpia todo el formulario */
    clearAll() {
      this.searchInput = "";
      this.patientFound = null;
      this.patientSelected = null;
      this.errorPatient = null;
      this.practiceId = "";
      this.practiceOptions = [];
      this.practiceOptionsLoaded = false;
      this.token = "";
      this.validationResult = null;
      this.errorCode = null;
    },

    /** @method clearResult - Solo limpia el resultado para reintentar */
    clearResult() {
      this.token = "";
      this.validationResult = null;
      this.errorCode = null;
    },

    /** @method getPatientSummary */
    getPatientSummary(member) {
      const parts = [];
      if (member.dni) parts.push(`DNI ${member.dni}`);
      if (member.memberNumber) parts.push(`Afil. ${member.memberNumber}`);
      if (member.plan?.name) parts.push(member.plan.name);
      return parts.join(" · ");
    },

    /** @method getInitials */
    getInitials(member) {
      return (
        (member.firstName || "")[0] + (member.lastName || "")[0]
      ).toUpperCase();
    },

    /** @method formatCurrency */
    formatCurrency(n) {
      return n != null ? `$${Number(n).toLocaleString("es-AR")}` : "-";
    },
  },
};
</script>

<template lang="pug">
  .validation-page
    .validation-card

      //- Steps
      .steps
        .step(:class="{ 'step--done': currentStep > 1, 'step--active': currentStep === 1 }")
          .step__number
            span(v-if="currentStep > 1") ✓
            span(v-else) 1
          span.step__label Afiliado
        .step__line(:class="{ 'step__line--done': currentStep > 1 }")
        .step(:class="{ 'step--done': currentStep > 2, 'step--active': currentStep === 2 }")
          .step__number
            span(v-if="currentStep > 2") ✓
            span(v-else) 2
          span.step__label Prestación
        .step__line(:class="{ 'step__line--done': currentStep > 2 }")
        .step(:class="{ 'step--active': currentStep === 3 }")
          .step__number 3
          span.step__label Validar

      //- Paso 1: Buscar afiliado
      h3.section-title Buscar afiliado
      .search-row
        el-input.search-field(
          v-model="searchInput"
          prefix-icon="el-icon-search"
          placeholder="Buscar por Nº de afiliado o DNI"
          clearable
          @keyup.enter.native="searchPatient"
        )
        el-button(type="primary" @click="searchPatient" :loading="searchingPatient") Buscar
        el-button(@click="clearAll") Limpiar

      //- Lista de resultados
      .patients-list(v-if="patientFound && patientFound.length && !patientSelected")
        .patient-item(
          v-for="(p, i) in patientFound"
          :key="p._id || i"
          @click="selectPatient(p)"
        )
          .patient-item__avatar {{ getInitials(p) }}
          .patient-item__info
            .patient-item__name {{ p.lastName }}, {{ p.firstName }}
            .patient-item__details {{ getPatientSummary(p) }}
          .patient-item__check

      //- Error afiliado
      .error-banner(v-if="errorPatient")
        i.el-icon-warning-outline
        span {{ errorPatient }}

      //- Afiliado seleccionado
      .selected-patient(v-if="patientSelected")
        .selected-patient__avatar {{ patientInitials }}
        .selected-patient__info
          span.selected-patient__name {{ patientName }}
          span.selected-patient__details {{ patientSummary }}
        button.selected-patient__clear(@click="clearAll")
          i.el-icon-close

      //- Paso 2 y 3: Prestación + Token
      template(v-if="patientSelected")
        .section-divider
        h3.section-title Prestación y validación

        el-form(label-position="top")
          el-form-item(label="Prestación")
            el-select(
              v-model="practiceId"
              filterable remote reserve-keyword
              placeholder="Buscar por código o nombre..."
              :remote-method="searchPractices"
              :loading="practiceSearchLoading"
              style="width:100%"
              @change="clearResult"
              @visible-change="v => { if (v && !practiceOptionsLoaded) { searchPractices(''); practiceOptionsLoaded = true } }"
            )
              el-option(
                v-for="o in practiceOptions"
                :key="o.value"
                :label="o.label"
                :value="o.value"
              )

        .token-row(v-if="practiceId")
          .token-field
            label.form-label-inline Token
            el-input.token-input(
              v-model="token"
              placeholder="_ _ _ _ _ _"
              clearable
              maxlength="6"
              @keyup.enter.native="validateCode"
            )
          el-button(
            type="primary"
            @click="validateCode"
            :loading="validatingCode"
            :disabled="!token || !practiceId"
            style="height:40px; padding: 0 24px"
          ) Validar
          el-button(
            @click="clearResult"
            :disabled="!token"
            style="height:40px"
          ) Limpiar

      //- Resultado aprobado
      .result-banner.result-banner--success(v-if="validationResult && validationResult.approved")
        .result-banner__icon.result-banner__icon--success ✓
        .result-banner__content
          .result-banner__title.result-banner__title--success Prestación autorizada
          .result-checks
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Afiliado activo
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Plan vigente
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Token válido
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Prestación cubierta
            span.result-check.result-check--valid
              span.dot.dot--valid
              | Prestador habilitado
          //- Valorización
          .valorization(v-if="validationResult.valorization && validationResult.valorization.amount")
            .valorization__item
              span.valorization__label Valor de la prestación
              span.valorization__amount {{ formatCurrency(validationResult.valorization.amount) }}
            .valorization__item(v-if="validationResult.valorization.hasCopago")
              span.valorization__label Copago del paciente
              span.valorization__amount {{ formatCurrency(validationResult.valorization.copagoAmount) }}
            .valorization__item(v-if="validationResult.valorization.hasCoseguro")
              span.valorization__label Coseguro
              span.valorization__amount {{ validationResult.valorization.coseguroPercent }}%
          el-button(type="text" style="margin-top:8px; color:#6b7294; font-size:12px" @click="clearAll") Nueva validación

      //- Resultado rechazado
      .result-banner.result-banner--error(v-if="validationResult && !validationResult.approved")
        .result-banner__icon.result-banner__icon--error ✕
        .result-banner__content
          .result-banner__title.result-banner__title--error Prestación no autorizada
          .result-checks
            .result-check(
              v-for="(label, i) in rejectionLabels"
              :key="i"
              class="result-check--invalid"
            )
              span.dot.dot--invalid
              | {{ label }}
          el-button(type="text" style="margin-top:8px; color:#6b7294; font-size:12px" @click="clearResult") Reintentar

      //- Error general
      .result-banner.result-banner--error(v-if="errorCode")
        .result-banner__icon.result-banner__icon--error !
        .result-banner__content
          .result-banner__title.result-banner__title--error {{ errorCode }}
</template>

<style lang="scss" scoped>
.validation-page {
  padding: 0;
}

.validation-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 32px;
  margin-top: 16px;
}

.steps {
  display: flex;
  align-items: center;
  margin-bottom: 28px;
}
.step {
  display: flex;
  align-items: center;
  gap: 8px;
  &__number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #6b7294;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #94a3b8;
  }
  &--active &__number {
    background: #002d5e;
    color: #fff;
  }
  &--active &__label {
    color: #002d5e;
  }
  &--done &__number {
    background: #05e5ba;
    color: #002d5e;
  }
  &--done &__label {
    color: #002d5e;
  }
  &__line {
    flex: 1;
    height: 2px;
    background: #e2e8f0;
    margin: 0 12px;
    &--done {
      background: #05e5ba;
    }
  }
}

.section-title {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #002d5e;
  margin: 0 0 16px;
}
.section-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 24px 0;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.search-field {
  flex: 1;
}

::v-deep .el-input__inner {
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  height: 40px;
  &:focus {
    border-color: #002d5e;
  }
  &::placeholder {
    color: #94a3b8;
  }
}

// Botones Element UI redondeados (8px) — homogeneiza con el resto del producto.
::v-deep .el-button {
  border-radius: 8px;
}
::v-deep .el-select .el-input__inner {
  border-radius: 8px;
}

.form-label-inline {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: block;
}

.patients-list {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 6px;
  margin-bottom: 14px;
  max-height: 200px;
  overflow-y: auto;
}
.patient-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  border: 1px solid transparent;
  &:hover {
    background: #f8fafc;
  }
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #002d5e;
    color: #fff;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__info {
    flex: 1;
    min-width: 0;
  }
  &__name {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #002d5e;
  }
  &__details {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 11px;
    color: #6b7294;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__check {
    width: 18px;
    height: 18px;
    border: 2px solid #cbd5e1;
    border-radius: 50%;
    flex-shrink: 0;
  }
}

.selected-patient {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #e8fdf8;
  border: 1px solid #05e5ba;
  border-radius: 10px;
  padding: 12px 16px;
  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #002d5e;
    color: #fff;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  &__name {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #002d5e;
  }
  &__details {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #6b7294;
    margin-top: 1px;
  }
  &__clear {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    &:hover {
      color: #002d5e;
      background: #f0f2f7;
    }
    i {
      font-size: 16px;
    }
  }
}

.token-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-top: 8px;
}
.token-field {
  flex: 1;
}
.token-input {
  ::v-deep .el-input__inner {
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 8px;
    text-align: center;
    color: #002d5e;
  }
}

.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  color: #991b1b;
  i {
    font-size: 18px;
    color: #dc2626;
    flex-shrink: 0;
  }
}

.result-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 10px;
  margin-top: 16px;
  &--success {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
  }
  &--error {
    background: #fef2f2;
    border: 1px solid #fecaca;
  }
  &__icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 700;
    flex-shrink: 0;
    &--success {
      background: #dcfce7;
      color: #16a34a;
    }
    &--error {
      background: #fee2e2;
      color: #dc2626;
    }
  }
  &__content {
    flex: 1;
  }
  &__title {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 8px;
    &--success {
      color: #166534;
    }
    &--error {
      color: #991b1b;
    }
  }
}

.result-checks {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.result-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 13px;
  font-weight: 500;
  &--valid {
    color: #16a34a;
  }
  &--invalid {
    color: #dc2626;
  }
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  &--valid {
    background: #16a34a;
  }
  &--invalid {
    background: #dc2626;
  }
}

.valorization {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #bbf7d0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
    color: #166534;
  }
  &__amount {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: #166534;
  }
}

@media (max-width: 768px) {
  .search-row {
    flex-wrap: wrap;
  }
  .token-row {
    flex-wrap: wrap;
  }
  .result-checks {
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
